import { Controller } from "@hotwired/stimulus"
import { renderSearchResults } from "../search_results"

export default class extends Controller {
  static targets = ["input", "sendButton", "status", "answer", "messages", "refreshButton", "retryButton", "results", "currentQuery", "suggestions"]
  static values = { url: String, searchUrl: String, conversationId: Number }

  connect() {
    this.resetRequests()
    this.conversation = null
    this.pendingRequest = null
    this.query = this.inputTarget.value.trim()
    this.element.dataset.searchActive = String(!!this.query)
    this.loadedQuery = this.resultsTarget.dataset.query
    this.onPopState = () => this.restoreLocation()
    window.addEventListener("popstate", this.onPopState)
    this.onScroll = () => {
      clearTimeout(this.scrollTimer)
      this.scrollTimer = setTimeout(() => { if (this.restoreScroll == null) history.replaceState({ ...history.state, scroll: window.scrollY }, "") }, 200)
    }
    window.addEventListener("scroll", this.onScroll, { passive: true })
    this.viewTurn = new URL(window.location.href).searchParams.get("turn")
    this.restorePage = history.state?.page || 1
    this.restoreScroll = history.state?.scroll
    if (this.conversationIdValue) {
      this.conversation = { id: this.conversationIdValue }
      this.refresh()
    }
  }

  disconnect() {
    this.statusTarget.dataset.loading = "false"
    window.removeEventListener("popstate", this.onPopState)
    window.removeEventListener("scroll", this.onScroll)
    clearTimeout(this.scrollTimer)
    this.abort.abort()
    this.resultsAbort?.abort()
    this.resultsAbort = null
    this.resultsLoading = null
    clearTimeout(this.timer)
  }

  resetRequests() {
    this.abort?.abort()
    this.resultsAbort?.abort()
    this.resultsAbort = null
    this.resultsLoading = null
    clearTimeout(this.timer)
    this.abort = new AbortController()
  }

  async request(path = "", options = {}) {
    const signal = this.abort.signal
    const response = await fetch(`${this.urlValue}${path}`, {
      credentials: "same-origin", ...options, signal,
      headers: { Accept: "application/json", "Content-Type": "application/json",
        "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || "" }
    })
    const data = response.status === 204 ? null : await response.json().catch(() => ({}))
    if (signal.aborted) throw new DOMException("Aborted", "AbortError")
    if (!response.ok) {
      const error = new Error(data.error || "request_failed")
      error.status = response.status
      throw error
    }
    return data
  }

  busy(value) {
    if (this.hasRetryButtonTarget && value) this.retryButtonTarget.hidden = true
    this.statusTarget.dataset.loading = String(value)
    this.sendButtonTarget.disabled = value
    this.answerTarget.setAttribute("aria-busy", String(value))
    this.suggestionsTarget.querySelectorAll("button[data-search-query]").forEach(button => { button.disabled = value })
  }

  showError(error) {
    if (error.name === "AbortError") return
    const messages = {
      allowance_reached: "The assistant’s usage limit has been reached. Here are matching results you can explore.",
      brief_changed: "This search changed. Check for an answer before trying again.",
      request_conflict: "Your search is still being processed. Check for an answer below.",
      assistant_unavailable: "An assistant answer is unavailable right now. You can browse matching content below.",
      invalid_request: "Please shorten your question and try again."
    }
    this.statusTarget.textContent = error.status === 401 ? "Sign in again for an assistant answer. Matching content is available below." :
      (messages[error.message] || "We couldn’t get an answer right now. You can browse matching content below.")
    this.busy(false)
    if (this.hasRetryButtonTarget) this.retryButtonTarget.hidden = !this.query || [401, 403, 422].includes(error.status) || ["request_conflict", "brief_changed"].includes(error.message)
    this.refreshButtonTarget.hidden = !this.conversation || (error.status >= 400 && error.status < 500 && ![409].includes(error.status))
    this.showFallback()
  }

  async ensureConversation() {
    if (this.conversation && !this.historical) return
    this.conversation = await this.request("", { method: "POST", body: JSON.stringify({}) })
    this.historical = false
  }

  submitOnEnter(event) {
    if (event.key !== "Enter" || event.isComposing || event.keyCode === 229) return
    event.preventDefault()
    if (event.repeat || this.sendButtonTarget.disabled) return
    this.inputTarget.form.requestSubmit(this.sendButtonTarget)
  }

  async send(event, { preserveResults = false } = {}) {
    event.preventDefault()
    const content = this.inputTarget.value.trim()
    if (!content || this.sendButtonTarget.disabled) return
    clearTimeout(this.scrollTimer)
    history.replaceState({ ...history.state, scroll: window.scrollY }, "")
    this.busy(true)
    this.element.dataset.searchActive = "true"
    this.statusTarget.textContent = "AI is thinking…"
    this.query = content
    if ((this.viewTurn || this.pendingRequest) && this.pendingRequest?.content !== content) {
      this.conversation = null
      this.viewTurn = null
      this.historical = false
      this.pendingRequest = null
    }
    if (!preserveResults) {
      this.loadedQuery = null
      this.restorePage = 1
      this.restoreScroll = null
      this.resultsAbort?.abort()
      this.resultsAbort = null
      this.resultsLoading = null
    }
    this.currentQueryTarget.textContent = content
    this.messagesTarget.replaceChildren()
    this.activeSources = []
    this.suggestionsTarget.replaceChildren()
    this.answerTarget.hidden = false
    if (!preserveResults) {
      this.resultsTarget.replaceChildren()
      this.loadResults(null, true)
    }
    try {
      await this.ensureConversation()
      if (!this.pendingRequest || this.pendingRequest.content !== content) {
        this.pendingRequest = { content, input_mode: "chat", request_id: crypto.randomUUID(), lock_version: this.conversation.lock_version }
      }
      const submitted = await this.request(`/${this.conversation.id}/message`, { method: "POST", body: JSON.stringify({ search_message: this.pendingRequest }) })
      this.viewTurn = String(submitted.id)
      this.historical = false
      const url = new URL(this.searchUrlValue, window.location.origin)
      url.searchParams.set("conversation", this.conversation.id)
      url.searchParams.set("turn", this.viewTurn)
      history.pushState({ scroll: 0, page: 1 }, "", url.href)
      this.pendingRequest = null
      if (this.inputTarget.value.trim() === content) this.inputTarget.value = ""
      this.inputTarget.placeholder = "Start a new search…"
      this.deadline = Date.now() + 120000
      await this.loadConversation()
    } catch (error) {
      if (error.status >= 400 && error.status < 500) this.pendingRequest = null
      this.showError(error)
    }
  }

  retry(event) {
    event.preventDefault()
    if (!this.query || this.sendButtonTarget.disabled) return
    const draft = this.inputTarget.value
    this.inputTarget.value = this.query
    const submission = this.send(event, { preserveResults: true })
    if (draft !== this.query) this.inputTarget.value = draft
    return submission
  }

  async refresh() {
    if (!this.conversation) return
    this.resetRequests()
    this.deadline = Date.now() + 120000
    await this.loadConversation()
  }

  async restoreLocation() {
    this.resetRequests()
    this.pendingRequest = null
    this.inputTarget.value = ""
    this.busy(true)
    const url = new URL(window.location.href)
    this.viewTurn = url.searchParams.get("turn")
    this.restorePage = history.state?.page || 1
    this.restoreScroll = history.state?.scroll || 0
    this.loadedQuery = null
    this.resultsTarget.replaceChildren()
    this.messagesTarget.replaceChildren()
    this.activeSources = []
    this.suggestionsTarget.replaceChildren()
    this.query = url.searchParams.get("q") || ""
    const id = url.searchParams.get("conversation")
    if (!id) { window.location.reload(); return }
    this.conversation = { id }
    this.deadline = Date.now() + 120000
    await this.loadConversation()
  }

  async loadConversation() {
    try {
      const suffix = this.viewTurn ? `?turn=${encodeURIComponent(this.viewTurn)}` : ""
      const data = await this.request(`/${this.conversation.id}${suffix}`)
      this.conversation = data
      this.historical = !!(this.viewTurn && data.latest_turn_id && String(data.latest_turn_id) !== String(this.viewTurn))
      const user = [...data.messages].reverse().find(item => item.role === "user")
      const latest = data.messages[data.messages.length - 1]
      if (!user) return
      this.viewTurn = String(user.id)
      this.query = user.content
      this.currentQueryTarget.textContent = user.content
      this.answerTarget.hidden = false
      this.element.dataset.searchActive = "true"
      this.renderMessages(latest?.role === "assistant" ? [latest] : [])
      const running = ["queued", "running"].includes(user.status)
      this.busy(running)
      this.refreshButtonTarget.hidden = !running || Date.now() < this.deadline
      if (running && Date.now() < this.deadline) {
        this.statusTarget.textContent = "AI is thinking…"
        this.loadResults(null, true)
        this.timer = setTimeout(() => this.loadConversation(), 1500)
      } else if (running) {
        this.statusTarget.dataset.loading = "false"
        this.statusTarget.textContent = "Your answer is taking longer than expected. You can explore the results while you wait."
        this.loadResults(null, true)
      } else {
        const failed = user.status === "failed" || latest?.status === "failed"
        if (this.hasRetryButtonTarget) this.retryButtonTarget.hidden = !failed
        this.statusTarget.textContent = user.status === "failed" || latest?.status === "failed" ? "An assistant answer isn’t available. Here are matching results." : ""
        this.loadResults()
      }
    } catch (error) { this.showError(error) }
  }

  showFallback() {
    if (this.query) this.loadResults(null, true)
  }

  resultPage(event) {
    const link = event.target.closest("a[data-search-page]")
    if (!link || !this.resultsTarget.contains(link)) return
    event.preventDefault()
    this.loadResults(Number(link.dataset.searchPage), this.resultsQuick)
  }

  async loadResults(page = null, quick = true) {
    const query = this.query
    const key = `${query}:${quick}`
    if (!query || (page === null && this.loadedQuery === key)) return
    page ||= 1
    if (this.resultsLoading?.key === key && this.resultsLoading.page === page) return
    this.resultsAbort?.abort()
    const abort = new AbortController()
    this.resultsAbort = abort
    this.resultsLoading = { key, page }
    this.resultsTarget.setAttribute("aria-busy", "true")
    try {
      const url = new URL(this.searchUrlValue, window.location.origin)
      url.search = new URLSearchParams({ q: query, page, results_only: "1" }).toString()
      if (quick) url.searchParams.set("quick", "1")
      else if (this.conversation && this.viewTurn) {
        url.searchParams.set("conversation", this.conversation.id)
        url.searchParams.set("turn", this.viewTurn)
      }
      const response = await fetch(url.href, { credentials: "same-origin", signal: abort.signal, headers: { Accept: "text/html" } })
      if (!response.ok) throw new Error("results_unavailable")
      const html = await response.text()
      if (abort.signal.aborted) return
      this.renderResults(html, page > 1)
      this.resultsTarget.querySelectorAll(".search-fit-explanation").forEach(element => {
        const text = element.textContent
        element.replaceChildren()
        this.linkedText(element, text, this.activeSources || [])
      })
      this.loadedQuery = key
      this.resultsQuick = quick
      if (this.restorePage > page) { await this.loadResults(page + 1, quick); return }
      history.replaceState({ ...history.state, page }, "")
      if (this.restoreScroll != null) {
        const top = this.restoreScroll
        this.restoreScroll = null
        requestAnimationFrame(() => window.scrollTo(0, top))
      }
    } catch (error) {
      if (!abort.signal.aborted) {
        if (page > 1) this.statusTarget.textContent = "Couldn’t load more results. Your current results are still available; try Show more results again."
        else this.resultsTarget.textContent = "Matching content is temporarily unavailable. Please try again."
      }
    } finally {
      if (this.resultsAbort === abort) {
        this.resultsLoading = null
        this.resultsTarget.setAttribute("aria-busy", "false")
      }
    }
  }

  renderResults(html, append = false) {
    renderSearchResults(this.resultsTarget, html, append)
  }

  sourceLink(source) {
    if (!source || typeof source.path !== "string" || !source.path.startsWith("/") || source.path.startsWith("//")) return null
    let url
    try { url = new URL(source.path, window.location.origin) } catch (_) { return null }
    if (url.origin !== window.location.origin) return null
    const link = document.createElement("a")
    link.href = url.href
    link.textContent = source.title
    return link
  }

  suggest(event) {
    const button = event.target.closest("button[data-search-query]")
    if (!button || !this.suggestionsTarget.contains(button) || this.sendButtonTarget.disabled) return
    this.inputTarget.value = button.dataset.searchQuery
    this.send(event)
  }

  linkedText(element, value, sources) {
    const text = String(value || "")
    // Only unambiguous, complete source titles become links. Never interpret HTML.
    const titles = new Map()
    sources.forEach(source => {
      const title = source.title?.trim()
      if (!title || title.length < 3 || !this.sourceLink(source)) return
      const key = title.toLocaleLowerCase()
      titles.set(key, titles.has(key) ? null : source)
    })
    const candidates = [...titles.values()].filter(Boolean).sort((a, b) => b.title.length - a.title.length)
    if (!candidates.length) { element.textContent = text; return }
    const escaped = candidates.map(source => source.title.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"))
    const pattern = new RegExp(escaped.join("|"), "giu")
    let offset = 0
    for (const match of text.matchAll(pattern)) {
      const start = match.index
      const end = start + match[0].length
      if (/[\p{L}\p{N}_]/u.test(text[start - 1] || "") || /[\p{L}\p{N}_]/u.test(text[end] || "")) continue
      const source = titles.get(match[0].toLocaleLowerCase())
      if (!source) continue
      element.append(document.createTextNode(text.slice(offset, start)))
      const link = this.sourceLink(source)
      link.textContent = match[0]
      element.append(link)
      offset = end
    }
    element.append(document.createTextNode(text.slice(offset)))
  }

  expandableAnswer(full, text, sources, recommendations = []) {
    const limit = 500
    if (recommendations.length <= 1 && (recommendations.length || text.length <= limit)) return full
    const wrapper = document.createElement("div")
    wrapper.dataset.controller = "expandable-text"
    wrapper.className = "search-message search-message--assistant"
    const preview = document.createElement("div")
    preview.dataset.expandableTextTarget = "preview"
    const continuation = document.createElement("div")
    if (recommendations.length > 1) {
      const nodes = [...full.children]
      preview.append(...nodes.slice(0, 2))
      continuation.append(...nodes.slice(2))
    } else {
      const beginning = document.createElement("p")
      const remainder = document.createElement("p")
      const boundary = text.lastIndexOf(" ", limit) > 0 ? text.lastIndexOf(" ", limit) : limit
      this.linkedText(beginning, text.slice(0, boundary), sources)
      this.linkedText(remainder, text.slice(boundary).trimStart(), sources)
      preview.append(beginning)
      continuation.append(remainder)
    }
    full = continuation
    full.dataset.expandableTextTarget = "full"
    full.hidden = true
    const button = document.createElement("button")
    button.type = "button"
    button.className = "expandable-text-toggle"
    button.dataset.expandableTextTarget = "button"
    button.dataset.action = "expandable-text#toggle"
    button.setAttribute("aria-expanded", "false")
    button.textContent = "Show more"
    wrapper.append(preview, full, button)
    return wrapper
  }

  renderMessages(messages) {
    this.activeSources = messages.flatMap(message => message.answer?.sources || [])
    this.suggestionsTarget.replaceChildren()
    const nodes = messages.map(message => {
      const container = document.createElement("div")
      container.className = `search-message search-message--${message.role === "user" ? "user" : "assistant"}`
      if (message.role === "user") {
        const title = document.createElement("strong")
        title.textContent = "You"
        container.append(title)
      }
      const answer = message.answer || {}
      const sourceList = answer.sources || []
      const text = document.createElement("p")
      this.linkedText(text, message.content, message.role === "assistant" ? sourceList : [])
      container.append(text)
      const seen = new Set()
      const recommendations = (answer.recommendations || []).filter(item => {
        if (seen.has(item.source_id)) return false
        const source = sourceList.find(source => source.source_id === item.source_id)
        if (!this.sourceLink(source)) return false
        seen.add(item.source_id)
        return true
      }).slice(0, 4)
      recommendations.forEach(item => {
        const paragraph = document.createElement("p")
        const name = document.createElement("strong")
        name.append(this.sourceLink(sourceList.find(source => source.source_id === item.source_id)))
        const explanation = document.createElement("span")
        this.linkedText(explanation, ` ${item.explanation}`, sourceList)
        paragraph.append(name, explanation)
        container.append(paragraph)
      })
      if (answer.clarification && !message.content?.includes(answer.clarification)) {
        const question = document.createElement("p")
        this.linkedText(question, answer.clarification, sourceList)
        container.append(question)
      }
      if (message.status === "failed") {
        const failure = document.createElement("p")
        const errors = {
          sources_changed: "Sources changed. Please search again.",
          request_interrupted: "This request was interrupted. Send a new message to try again.",
          provider_not_configured: "The assistant needs server configuration. Please contact the site administrator.",
          provider_authentication_failed: "The assistant could not authenticate with its AI provider. Please contact the site administrator.",
          provider_responses_permission_missing: "The AI provider has not granted permission to generate answers. Please contact the site administrator.",
          provider_model_request_permission_missing: "The AI provider has not granted permission to use the model. Please contact the site administrator.",
          provider_model_read_permission_missing: "The AI provider has not granted permission to read model information. Please contact the site administrator.",
          provider_key_rejected: "The AI provider rejected the assistant's credential. Please contact the site administrator.",
          provider_ip_denied: "The AI provider blocked this server's network access. Please contact the site administrator.",
          provider_organization_denied: "The AI provider denied organization or project access. Please contact the site administrator.",
          provider_permission_missing: "The AI provider has not granted the required permissions. Please contact the site administrator.",
          provider_access_denied: "The AI provider denied access. Please contact the site administrator.",
          provider_not_found: "The configured AI model or endpoint is unavailable. Please contact the site administrator.",
          provider_invalid_request: "The AI provider rejected the request configuration. Please contact the site administrator.",
          provider_quota_exceeded: "The AI provider's credits or usage limit have been reached. Please contact the site administrator.",
          provider_rate_limited: "The AI provider is receiving too many requests. Please wait before sending another message.",
          provider_connection_failed: "The connection to the AI provider failed. Please try again later.",
          provider_unavailable: "The AI provider is unavailable. Please try again later.",
          provider_incomplete_response: "The AI provider did not return a complete usable response. Please try a shorter request."
        }
        failure.textContent = errors[message.error_code] || "This request could not be completed. Please try again."
        container.append(failure)
      }
      const queries = (answer.suggested_queries || []).filter(query => typeof query === "string" && query.trim() && query.length <= 200).slice(0, 2)
      if (queries.length) {
        const suggestions = document.createElement("div")
        suggestions.className = "search-suggestions"
        const label = document.createElement("strong")
        label.textContent = "Explore further"
        suggestions.append(label)
        queries.forEach(query => {
          const button = document.createElement("button")
          button.type = "button"
          button.dataset.searchQuery = query
          button.textContent = query
          button.disabled = this.sendButtonTarget.disabled
          suggestions.append(button)
        })
        this.suggestionsTarget.replaceChildren(suggestions)
      }
      const fullText = [message.content, ...recommendations.map(item => {
        const source = sourceList.find(source => source.source_id === item.source_id)
        return `${source.title}: ${item.explanation}`
      }), answer.clarification || ""].join("\n\n")
      return message.role === "assistant" && message.status !== "failed" ? this.expandableAnswer(container, fullText, sourceList, recommendations) : container
    })
    this.messagesTarget.replaceChildren(...nodes)
  }
}
